import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Form,
  Tabs,
  TabsProps,
  notification,
  Popconfirm,
  Input,
} from "antd";
import { useParams } from "react-router-dom";
import { TbEditCircle } from "react-icons/tb";
import JoditEditor from "jodit-react";
import { editRichTextByID, getRichTextByID } from "../actions";

type Props = {
  fetchData: () => void;
};

interface FormValues {
  title: {
    en: string;
    ar: string;
    fr: string;
  };
  content: {
    en: string;
    ar: string;
    fr: string;
  };
}

const EditCategoryRichText: React.FC<Props> = ({ fetchData }) => {
  const [form] = Form.useForm<FormValues>();
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const { id, categoryId, richtextid } = useParams();

  const fetchItem = async () => {
    if (!richtextid) return;
    setIsLoading(true);
    try {
      const rich_text = await getRichTextByID(richtextid);
      if (rich_text) {
        form.setFieldsValue({
          title: {
            en: rich_text.Title.En || "",
            ar: rich_text.Title.Ar || "",
            fr: rich_text.Title.Fr || "",
          },
          content: {
            en: rich_text.Content.En || "",
            ar: rich_text.Content.Ar || "",
            fr: rich_text.Content.Fr || "",
          },
        });
      }
    } catch (err) {
      api.error({ message: "Failed to fetch website" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsLoading(true);
      const success = await editRichTextByID(
        richtextid!,
        values.title,
        values.content
      );
      if (success) {
        fetchData();
        api.success({ message: "Rich text updated successfully" });
      }
    } catch (err: any) {
      api.error({
        message: "Failed to update rich text",
        description:
          err?.response?.errors?.[0]?.message ||
          err?.message ||
          "Unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItem();
  }, [richtextid]);

  const languages = ["en", "ar", "fr"] as const;

  const tabItems: TabsProps["items"] = languages.map((lang) => ({
    key: lang,
    label: lang.toUpperCase(),
    children: (
      <div>
        <Form.Item name={["title", lang]} label={`Title (${lang})`}>
          <Input placeholder={`Write title in ${lang.toUpperCase()} ...`} />
        </Form.Item>
        <Form.Item name={["content", lang]} label={`Rich text (${lang})`}>
          <JoditEditor
            ref={useRef(null)}
            value={form.getFieldValue(["content", lang])}
            onBlur={(newContent) => {
              form.setFieldValue(["content", lang], newContent);
            }}
            config={{
              readonly: false,
              direction: lang === "ar" ? "rtl" : "ltr",
              placeholder: `Write rich text in ${lang.toUpperCase()}...`,
              height: "50vh",
            }}
          />
        </Form.Item>
      </div>
    ),
  }));

  return (
    <Form form={form} layout="vertical">
      {contextHolder}
      <Tabs defaultActiveKey="en" items={tabItems} />

      <Form.Item shouldUpdate>
        {() => (
          <Popconfirm
            title="Are you sure you want to edit the rich text?"
            onConfirm={handleSubmit}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<TbEditCircle />}
              loading={isLoading}
              type="primary"
              className="w-full"
            >
              {isLoading ? "Saving..." : "Edit Rich Text"}
            </Button>
          </Popconfirm>
        )}
      </Form.Item>
    </Form>
  );
};

export default EditCategoryRichText;
